import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { getTruckById, updateTruck } from '../../store/trucks';
import { fetchBuildingsUser } from '../../store/buildings';

const TRUCK_TYPES = {
  'daycab_1_axle': 'Daycab 1 Axle',
  'daycab_2_axle': 'Daycab 2 Axle',
  'sleeper_1_axle': 'Sleeper 1 Axle',
  'sleeper_2_axle': 'Sleeper 2 Axle',
  'boxtruck': 'Boxtruck'
};

function UpdateTruck() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const sessionUser = useSelector(state => state.session.user);
  const allUsers = useSelector(state => state.users);
  const buildings = useSelector((state) => state.buildings || []);
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [formData, setFormData] = useState({
    assigned_user_id: '',
    truck_number: '',
    truck_location: '',
    type: '',
    license_plate: '',
    license_state: '',
    year: '',
    make: '',
    model: '',
    red_tagged: false,
    yellow_tagged: false,
    in_shop: false,
    tagged_description: '',
    last_inspection: '',
    next_inspection: '',
    last_maintenance: '',
    next_maintenance: ''
  });

  // Define available locations
  const locations = ['Yard', 'Road', 'In Shop'];

  // Filter users with driver numbers
  const driversWithNumbers = allUsers.filter(user => {
    // Check if user has a driver number
    const hasDriverNumber = user.driver_number;

    // If current user is admin, show all drivers
    if (sessionUser.is_admin) {
      return hasDriverNumber;
    }

    // For non-admin users, show only drivers from their account
    return hasDriverNumber && user.account_id === sessionUser.account_id;
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const truckData = await dispatch(getTruckById(id));
        if (!truckData) {
          throw new Error("Failed to fetch truck data");
        }

        // Fetch buildings for the account
        if (truckData.account_id) {
          await dispatch(fetchBuildingsUser(truckData.account_id));
        }

        // Set the form data
        setFormData({
          ...truckData,
          last_inspection: truckData.last_inspection ? new Date(truckData.last_inspection).toISOString().split('T')[0] : '',
          next_inspection: truckData.next_inspection ? new Date(truckData.next_inspection).toISOString().split('T')[0] : '',
          last_maintenance: truckData.last_maintenance ? new Date(truckData.last_maintenance).toISOString().split('T')[0] : '',
          next_maintenance: truckData.next_maintenance ? new Date(truckData.next_maintenance).toISOString().split('T')[0] : ''
        });

        // Set selected building if truck location includes a building
        if (truckData.truck_location && !truckData.in_shop) {
          const [buildingName] = truckData.truck_location.split('-');
          const matchingBuilding = buildings.find(b => b.building_name === buildingName);
          if (matchingBuilding) {
            setSelectedBuilding(matchingBuilding);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, dispatch, sessionUser?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formattedData = {
        ...formData,
        year: parseInt(formData.year),
        assigned_user_id: formData.assigned_user_id ? parseInt(formData.assigned_user_id) : null,
        id: parseInt(id)
      };

      // Format dates if they exist
      ['last_inspection', 'next_inspection', 'last_maintenance', 'next_maintenance'].forEach(field => {
        if (formattedData[field]) {
          formattedData[field] = new Date(formattedData[field]).toISOString().split('T')[0];
        }
      });

      // Basic validation
      if (!formattedData.truck_number || !formattedData.truck_location ||
          !formattedData.type || !formattedData.license_plate ||
          !formattedData.license_state || !formattedData.year ||
          !formattedData.make || !formattedData.model) {
        window.alert('Please fill out all required fields');
        return;
      }

      // Validate tagged description if truck is tagged
      if ((formattedData.red_tagged || formattedData.yellow_tagged) && !formattedData.tagged_description) {
        window.alert('Tagged description is required when a truck is tagged');
        return;
      }

      const result = await dispatch(updateTruck(formattedData));
      if (result) {
        history.push(`/trucks/${id}`);
      }
    } catch (error) {
      console.error('Error updating truck:', error);
      window.alert('Failed to update truck. Please try again.');
    }
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({
      ...prev,
      red_tagged: status === 'red',
      yellow_tagged: status === 'yellow',
      in_shop: status === 'shop',
      // If in_shop is selected, automatically set location to "Shop"
      truck_location: status === 'shop' ? 'Shop' : prev.truck_location
    }));
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const name = e.target.name;

    if (name === 'red_tagged' && value === true) {
      // If red_tagged is checked, uncheck yellow_tagged
      setFormData(prev => ({
        ...prev,
        [name]: value,
        yellow_tagged: false
      }));
    } else if (name === 'yellow_tagged' && value === true) {
      // If yellow_tagged is checked, uncheck red_tagged
      setFormData(prev => ({
        ...prev,
        [name]: value,
        red_tagged: false
      }));
    } else if (name === 'in_shop' && value === true) {
      // If in_shop is checked, set location to "Shop"
      setFormData(prev => ({
        ...prev,
        [name]: value,
        truck_location: 'Shop'
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLocationChange = (locationType, value) => {
    if (locationType === 'building') {
      const building = buildings.find(b => b.building_name === value);
      setSelectedBuilding(building);
      // Clear the truck location until a door is selected
      setFormData(prev => ({
        ...prev,
        truck_location: '',
        in_shop: false
      }));
    } else {
      setSelectedBuilding(null);
      setFormData(prev => ({
        ...prev,
        truck_location: value,
        in_shop: value === 'In Shop'
      }));
    }
  };

  const handleDoorSelection = (doorNumber) => {
    if (selectedBuilding) {
      setFormData(prev => ({
        ...prev,
        truck_location: `${selectedBuilding.building_name}-${doorNumber}`
      }));
    }
  };

  if (loading) return <div className="p-5 text-7xl">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-8xl font-bold mb-10 text-center text-blue-600">Update Truck</h1>

        <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Basic Information</h2>

              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Assigned Driver
                </label>
                <select
                  name="assigned_user_id"
                  value={formData.assigned_user_id}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Driver</option>
                  {driversWithNumbers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.alpha_code} - {driver.email} (Driver #{driver.driver_number})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Truck Number
                </label>
                <input
                  type="text"
                  name="truck_number"
                  value={formData.truck_number}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Location
                </label>
                <div className="space-y-6">
                  {/* Standard locations */}
                  <div className="flex flex-wrap gap-4">
                    {locations.map(location => (
                      <label key={location} className="flex-1 flex items-center justify-center bg-gray-50 px-6 py-3 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer min-w-[150px]">
                        <input
                          type="radio"
                          name="location"
                          value={location}
                          checked={formData?.truck_location === location}
                          onChange={(e) => handleLocationChange('standard', e.target.value)}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-xl text-gray-700">{location}</span>
                      </label>
                    ))}
                  </div>

                  {/* Building locations */}
                  {buildings.length > 0 && (
                    <div className="mt-6">
                      <div className="flex flex-wrap gap-4">
                        {buildings
                          .filter(building => building.account_id === formData?.account_id)
                          .map(building => (
                            <label key={building.id} className="flex-1 flex items-center justify-center bg-gray-50 px-6 py-3 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer min-w-[150px]">
                              <input
                                type="radio"
                                name="location"
                                value={building.building_name}
                                checked={selectedBuilding?.id === building.id}
                                onChange={(e) => handleLocationChange('building', e.target.value)}
                                className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-3 text-xl text-gray-700">{building.building_name}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Door number dropdown */}
                  {selectedBuilding && (
                    <div className="mt-6">
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Select Door Number for {selectedBuilding.building_name}:
                      </label>
                      <select
                        className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData?.truck_location?.split('-')[1] || ''}
                        onChange={(e) => handleDoorSelection(e.target.value)}
                      >
                        <option value="">Select a door number</option>
                        {Array.from({ length: selectedBuilding.building_door_number }, (_, i) => i + 1).map(door => (
                          <option key={door} value={door}>
                            Door {door}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Status</h2>

              <div className="space-y-4">
                <div className="flex flex-col gap-4">
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      name="status"
                      checked={!formData.red_tagged && !formData.yellow_tagged && !formData.in_shop}
                      onChange={() => handleStatusChange("")}
                      className="w-5 h-5"
                    />
                    Available
                  </label>
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      name="status"
                      checked={formData.red_tagged}
                      onChange={() => handleStatusChange("red")}
                      className="w-5 h-5"
                    />
                    Red Tagged
                  </label>
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      name="status"
                      checked={formData.yellow_tagged}
                      onChange={() => handleStatusChange("yellow")}
                      className="w-5 h-5"
                    />
                    Yellow Tagged
                  </label>
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      name="status"
                      checked={formData.in_shop}
                      onChange={() => handleStatusChange("shop")}
                      className="w-5 h-5"
                    />
                    In Shop
                  </label>
                </div>

                <div>
                  <label className="block text-2xl font-medium text-gray-700 mb-2">
                    Tagged Description
                  </label>
                  <textarea
                    name="tagged_description"
                    value={formData.tagged_description}
                    onChange={handleChange}
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Vehicle Details</h2>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  License Plate
                </label>
                <input
                  type="text"
                  name="license_plate"
                  value={formData.license_plate}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  License State
                </label>
                <input
                  type="text"
                  name="license_state"
                  value={formData.license_state}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Make
                </label>
                <input
                  type="text"
                  name="make"
                  value={formData.make}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Model
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Type</option>
                  {Object.entries(TRUCK_TYPES).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Maintenance Dates</h2>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Last Inspection
                </label>
                <input
                  type="date"
                  name="last_inspection"
                  value={formData.last_inspection}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Next Inspection
                </label>
                <input
                  type="date"
                  name="next_inspection"
                  value={formData.next_inspection}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Last Maintenance
                </label>
                <input
                  type="date"
                  name="last_maintenance"
                  value={formData.last_maintenance}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
              <div>
                <label className="block text-2xl font-medium text-gray-700 mb-2">
                  Next Maintenance
                </label>
                <input
                  type="date"
                  name="next_maintenance"
                  value={formData.next_maintenance}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={() => history.push(`/trucks/${id}`)}
              className="px-6 py-3 text-xl text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300"
            >
              Cancel
            </button>
            <button id = 'update-truck-button'
              type="submit"
              className="px-6 py-3 text-xl bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Update Truck
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateTruck;
